import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtError } from "@/lib/api";
import { METIERS, L } from "@/constants/labels";
import { ACOMPTE_RATE, tierFor, modesFor, buildEcheancier } from "@/lib/echeancierRules";
import { PendingDocsPicker, uploadPendingDocs } from "@/components/BesoinDocs";

const BUDGET_TYPES = [
  { v: "sur_descriptif", t: "À chiffrer sur descriptif" },
  { v: "indicatif", t: "Budget indicatif" },
  { v: "fourchette", t: "Fourchette" },
  { v: "montant", t: "Montant fixe" },
];

const DUREE_UNITES = [
  { v: "jours", t: "jour(s)" },
  { v: "semaines", t: "semaine(s)" },
  { v: "mois", t: "mois" },
];

export default function NouveauBesoin() {
  const nav = useNavigate();
  const [f, setF] = useState({
    titre: "", metier: "", specialite: "", sous_specialite: "",
    description: "", rue: "", ville: "", code_postal: "",
    budget_type: "sur_descriptif", budget_ht: "", budget_min: "", budget_max: "",
    duree_valeur: "", duree_unite: "jours", duree_texte: "",
    date_debut: "", date_fin: "",
  });
  // Assistant d'échéancier — configuré par le donneur d'ordre selon montant.
  // Les règles sont enforcées côté backend (validator Pydantic).
  const [scheme, setScheme] = useState({ modeId: null, situations: [] });
  const totalHT = Number(f.budget_ht) || 0;
  const availableModes = useMemo(() => modesFor(totalHT), [totalHT]);
  const tier = tierFor(totalHT);
  const currentMode = useMemo(() =>
    availableModes.find((m) => m.id === scheme.modeId) || null, [availableModes, scheme.modeId]);
  const situationCount = currentMode
    ? (currentMode.situations !== null ? currentMode.situations : scheme.situations.length)
    : 0;
  const echeancierBuilt = useMemo(() => {
    if (!totalHT || !currentMode) return { lines: [], ok: false, error: null };
    // Ne prend en compte que les situations pertinentes pour le mode
    const sits = scheme.situations.slice(0, situationCount).map((v) => Number(v) || 0);
    return buildEcheancier(totalHT, sits);
  }, [totalHT, currentMode, scheme.situations, situationCount]);

  // Réinitialise le mode quand le montant change de tier (les modes autorisés changent)
  const setBudgetHt = (v) => {
    setF((old) => ({ ...old, budget_ht: v }));
    setScheme({ modeId: null, situations: [] });
  };
  const [dureeTexteOn, setDureeTexteOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const specs = f.metier ? Object.keys(METIERS[f.metier] || {}) : [];
  const sspecs = f.metier && f.specialite ? (METIERS[f.metier]?.[f.specialite] || []) : [];
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const [pendingDocs, setPendingDocs] = useState([]);
  const submit = async (e) => {
    e.preventDefault();
    if (f.budget_type === "fourchette" && f.budget_min && f.budget_max && Number(f.budget_min) > Number(f.budget_max)) {
      return toast.error("Le budget minimum doit être inférieur au maximum.");
    }
    if (!f.date_debut || !f.date_fin) {
      return toast.error("Les dates de démarrage et de fin prévisionnelle sont obligatoires.");
    }
    if (f.date_fin < f.date_debut) {
      return toast.error("La date de fin doit être postérieure à la date de début.");
    }
    setBusy(true);
    try {
      // Si un mode d'échéancier a été choisi (budget montant), on utilise le
      // schéma calculé. Sinon (types indicatif / fourchette / sur_descriptif),
      // on envoie un échéancier vide — le backend fera un jalon "Solde"
      // unique par défaut à l'acceptation.
      let echeancier_payload = [];
      if (f.budget_type === "montant" && currentMode) {
        if (!echeancierBuilt.ok) {
          setBusy(false);
          return toast.error(echeancierBuilt.error || "Échéancier incohérent");
        }
        echeancier_payload = echeancierBuilt.lines.map((l, i) => ({
          ...l, ordre: i + 1, montant_ht: Number(l.montant_ht),
        }));
      }
      const payload = {
        ...f,
        budget_ht: (f.budget_type === "indicatif" || f.budget_type === "montant") && f.budget_ht ? Number(f.budget_ht) : null,
        budget_min: f.budget_type === "fourchette" && f.budget_min ? Number(f.budget_min) : null,
        budget_max: f.budget_type === "fourchette" && f.budget_max ? Number(f.budget_max) : null,
        duree_valeur: !dureeTexteOn && f.duree_valeur ? Number(f.duree_valeur) : null,
        duree_unite: !dureeTexteOn ? f.duree_unite : null,
        duree_texte: dureeTexteOn ? (f.duree_texte || null) : null,
        echeancier: echeancier_payload,
      };
      const { data } = await api.post("/besoins", payload);
      const geoMsg = data.lat && data.lng ? " (géocodage OK)" : " (géocodage : adresse non résolue)";
      if (pendingDocs.length) {
        toast.message(`Envoi de ${pendingDocs.length} pièce(s) jointe(s)…`);
        const r = await uploadPendingDocs(api, data.id, pendingDocs);
        if (r.errors.length) toast.error(`${r.ok} fichier(s) joint(s), ${r.errors.length} en échec : ${r.errors.join(" · ")}`, { duration: 12000 });
        else toast.success(`${r.ok} pièce(s) jointe(s) ajoutée(s)`);
      }
      toast.success(`Besoin publié${geoMsg}`);
      nav(`/app/chantiers/${data.id}`);
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-2xl">
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">{L.wording.nouveauBesoinH1}</h1>
      <div className="mt-6 space-y-4">
        <div>
          <label className="label">Titre</label>
          <input required className="input" value={f.titre} onChange={set("titre")} data-testid="besoin-titre" />
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="label">Corps de métier</label>
            <select required className="select" value={f.metier} onChange={(e) => setF({ ...f, metier: e.target.value, specialite: "", sous_specialite: "" })} data-testid="besoin-metier">
              <option value="">Sélectionner</option>{Object.keys(METIERS).map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Spécialité</label>
            <select className="select" value={f.specialite} onChange={(e) => setF({ ...f, specialite: e.target.value, sous_specialite: "" })} disabled={!f.metier}>
              <option value="">Toutes</option>{specs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Sous-spécialité</label>
            <select className="select" value={f.sous_specialite} onChange={set("sous_specialite")} disabled={!f.specialite}>
              <option value="">Toutes</option>{sspecs.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <textarea required rows={5} className="textarea" value={f.description} onChange={set("description")} data-testid="besoin-desc"
            placeholder="Descriptif des travaux, contraintes, existants…" />
        </div>

        {/* Localisation */}
        <fieldset className="border border-[#E4E7EB] rounded-[4px] p-3">
          <legend className="text-sm font-semibold text-[#1F2933] px-2">Localisation du chantier</legend>
          <div className="grid md:grid-cols-3 gap-3 mt-2">
            <div className="md:col-span-2">
              <label className="label">Rue</label>
              <input className="input" value={f.rue} onChange={set("rue")} data-testid="besoin-rue" placeholder="Ex. Rue de la République" />
            </div>
            <div>
              <label className="label">Code postal</label>
              <input required pattern="\d{5}" className="input" value={f.code_postal} onChange={set("code_postal")} data-testid="besoin-cp" />
            </div>
            <div className="md:col-span-3">
              <label className="label">Ville</label>
              <input required className="input" value={f.ville} onChange={set("ville")} data-testid="besoin-ville" />
            </div>
          </div>
          <p className="mt-2 text-xs text-[#3E4C59]">{L.wording.nouveauBesoinGeoHint}</p>
        </fieldset>

        {/* Budget */}
        <fieldset className="border border-[#E4E7EB] rounded-[4px] p-3">
          <legend className="text-sm font-semibold text-[#1F2933] px-2">Budget (facultatif)</legend>
          <div>
            <label className="label">Mode de budget</label>
            <select className="select" value={f.budget_type} onChange={set("budget_type")} data-testid="besoin-budget-type">
              {BUDGET_TYPES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
            </select>
          </div>
          {(f.budget_type === "indicatif" || f.budget_type === "montant") && (
            <div className="mt-3">
              <label className="label">{f.budget_type === "indicatif" ? "Budget indicatif HT (€)" : "Montant HT (€)"}</label>
              <input type="number" min="0" step="0.01" className="input" value={f.budget_ht} onChange={(e) => setBudgetHt(e.target.value)} data-testid="besoin-budget" />
            </div>
          )}
          {f.budget_type === "fourchette" && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label">De (€ HT)</label>
                <input type="number" min="0" step="0.01" className="input" value={f.budget_min} onChange={set("budget_min")} data-testid="besoin-budget-min" />
              </div>
              <div>
                <label className="label">À (€ HT)</label>
                <input type="number" min="0" step="0.01" className="input" value={f.budget_max} onChange={set("budget_max")} data-testid="besoin-budget-max" />
              </div>
            </div>
          )}
          {f.budget_type === "sur_descriptif" && (
            <p className="mt-3 text-xs text-[#3E4C59]">{L.wording.nouveauBesoinSurDescriptif}</p>
          )}
        </fieldset>

        {/* Durée */}
        <fieldset className="border border-[#E4E7EB] rounded-[4px] p-3">
          <legend className="text-sm font-semibold text-[#1F2933] px-2">Durée</legend>
          <label className="flex items-center gap-2 text-sm mb-2">
            <input type="checkbox" checked={dureeTexteOn} onChange={(e) => setDureeTexteOn(e.target.checked)} data-testid="besoin-duree-texte-toggle" />
            Durée à définir / cas particulier
          </label>
          {dureeTexteOn ? (
            <input className="input" value={f.duree_texte} onChange={set("duree_texte")} data-testid="besoin-duree-texte"
              placeholder="Ex. 2 jours par semaine pendant un mois" />
          ) : (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="label">Valeur</label>
                <input type="number" min="0" className="input" value={f.duree_valeur} onChange={set("duree_valeur")} data-testid="besoin-duree-val" />
              </div>
              <div className="col-span-2">
                <label className="label">Unité</label>
                <select className="select" value={f.duree_unite} onChange={set("duree_unite")} data-testid="besoin-duree-unite">
                  {DUREE_UNITES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                </select>
              </div>
            </div>
          )}
        </fieldset>

        {/* Dates */}
        <fieldset className="border border-[#E4E7EB] rounded-[4px] p-3">
          <legend className="text-sm font-semibold text-[#1F2933] px-2">Dates clés</legend>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="label">Démarrage prévu <span className="text-[#C0392B]">*</span></label>
              <input type="date" required className="input" value={f.date_debut} onChange={set("date_debut")} data-testid="besoin-date-debut" />
            </div>
            <div>
              <label className="label">Fin prévisionnelle <span className="text-[#C0392B]">*</span></label>
              <input type="date" required className="input" value={f.date_fin} onChange={set("date_fin")} data-testid="besoin-date-fin" />
            </div>
          </div>
          <p className="mt-2 text-xs text-[#3E4C59]">{L.wording.nouveauBesoinDatesContractuelles}</p>
        </fieldset>

        {/* Échéancier — assistant wizard aligné sur les règles 2026-02 :
            <3000€ → acompte30 + solde ; 3000-6000€ → 2 options ; >6000€ → N situations */}
        <fieldset className="border border-[#E4E7EB] rounded-[4px] p-3" data-testid="echeancier-wizard">
          <legend className="text-sm font-semibold text-[#1F2933] px-2">Échéancier de paiement</legend>
          {f.budget_type !== "montant" ? (
            <p className="text-xs text-[#3E4C59]">
              Sélectionnez « Montant fixe » dans le bloc <b>Budget</b> pour activer l'assistant d'échéancier avec acompte 30 % systématique. Les besoins « à chiffrer sur descriptif » et « fourchette » génèrent un jalon Solde unique à l'acceptation.
            </p>
          ) : totalHT <= 0 ? (
            <p className="text-xs text-[#3E4C59]">Saisissez d'abord un <b>montant HT</b> dans le bloc Budget.</p>
          ) : (
            <div className="space-y-3">
              <div className="border-l-4 border-[#1B3A5C] bg-[#EFF3F8] p-3 text-[13px] text-[#1F2933]">
                <p><b>Acompte 30 % systématique</b> — libéré au démarrage effectif du chantier (sans validation de jalon).</p>
                <p className="mt-1">
                  Montant total <b>{totalHT.toFixed(2)} € HT</b> — palier <b>{tier === 1 ? "< 3 000 €" : tier === 2 ? "3 000 à 6 000 €" : "> 6 000 €"}</b>. Les schémas non autorisés à ce palier sont grisés.
                </p>
              </div>
              {/* Choix du mode */}
              <div>
                <div className="label text-xs mb-1">Schéma d'échéancier</div>
                <div className="space-y-1">
                  {availableModes.map((m) => (
                    <label key={m.id} className="flex items-start gap-2 cursor-pointer text-sm" data-testid={`echeancier-mode-${m.id}`}>
                      <input type="radio" name="ech-mode" value={m.id}
                             checked={scheme.modeId === m.id}
                             onChange={() => setScheme({ modeId: m.id,
                               situations: m.situations && m.situations > 0
                                 ? Array(m.situations).fill("") : (m.situations === null ? [""] : []) })}
                             className="mt-1"
                             data-testid={`echeancier-mode-input-${m.id}`} />
                      <span>{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tier 3 : nombre de situations réglable */}
              {currentMode && currentMode.situations === null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm">Nombre de situations intermédiaires :</span>
                  <button type="button" className="btn-outline text-xs"
                          onClick={() => setScheme((s) => ({ ...s, situations: s.situations.slice(0, -1) || [""] }))}
                          disabled={scheme.situations.length <= 1}
                          data-testid="ech-sit-minus">−</button>
                  <span data-testid="ech-sit-count" className="min-w-[2ch] text-center">{scheme.situations.length}</span>
                  <button type="button" className="btn-outline text-xs"
                          onClick={() => setScheme((s) => ({ ...s, situations: [...s.situations, ""] }))}
                          data-testid="ech-sit-plus">+</button>
                </div>
              )}

              {/* Saisie des montants de situations */}
              {currentMode && situationCount > 0 && (
                <div className="space-y-2">
                  <div className="label text-xs">Montant HT de chaque situation intermédiaire</div>
                  {Array.from({ length: situationCount }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs w-24">Situation {i + 1}</span>
                      <input type="number" step="0.01" min="0.01" className="input w-40"
                             value={scheme.situations[i] || ""}
                             onChange={(ev) => setScheme((s) => {
                               const next = [...s.situations];
                               next[i] = ev.target.value;
                               return { ...s, situations: next };
                             })}
                             data-testid={`ech-sit-input-${i}`} />
                      <span className="text-xs text-[#3E4C59]">€ HT</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Aperçu calculé */}
              {currentMode && (
                <div className="border border-[#E4E7EB] rounded-[6px] p-3 bg-white" data-testid="echeancier-preview">
                  <div className="text-xs uppercase text-[#3E4C59] mb-2">Aperçu de l'échéancier</div>
                  <ul className="space-y-1 text-sm">
                    {echeancierBuilt.lines.map((l, i) => (
                      <li key={i} className="flex justify-between border-b border-[#F0F2F4] py-1 last:border-none"
                          data-testid={`ech-preview-line-${i}`}>
                        <span>
                          {l.type === "acompte" && <b>Acompte 30 %</b>}
                          {l.type === "situation" && <span>Situation {i}</span>}
                          {l.type === "solde" && <b>Solde à réception</b>}
                          <span className="text-xs text-[#3E4C59] ml-2">({l.type})</span>
                        </span>
                        <b>{Number(l.montant_ht).toFixed(2)} €</b>
                      </li>
                    ))}
                  </ul>
                  {!echeancierBuilt.ok && (
                    <div className="mt-2 text-xs text-[#C0392B]" data-testid="echeancier-preview-error">
                      {echeancierBuilt.error}
                    </div>
                  )}
                  {echeancierBuilt.ok && (
                    <div className="mt-2 text-xs text-[#1B3A5C]" data-testid="echeancier-preview-ok">
                      {L.wording.nouveauBesoinCoherent(`${echeancierBuilt.lines.reduce((s, l) => s + Number(l.montant_ht), 0).toFixed(2)} €`)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </fieldset>

        <PendingDocsPicker files={pendingDocs} setFiles={setPendingDocs} />
        <button disabled={busy} className="btn-primary" data-testid="besoin-submit">{busy ? "…" : L.wording.nouveauBesoinSubmit}</button>
      </div>
    </form>
  );
}
